import { useState, useEffect } from 'react'
import axios from 'axios'
import { getApiBaseUrl } from '@/lib/apiBase'

const API_URL = getApiBaseUrl()

export default function PromoPopup({
  onClose,
  popupConfig,
  admissionCount,
  previewMode = false
}) {
  const [visible, setVisible] = useState(false)
  const [animateIn, setAnimateIn] = useState(false)

  useEffect(() => {
    requestAnimationFrame(() => {
      setVisible(true)
      setTimeout(() => setAnimateIn(true), 50)
    })
    if (!previewMode) recordInteraction('shown')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const recordInteraction = async (action) => {
    if (previewMode) return
    try {
      await axios.post(`${API_URL}/users/me/popup-interaction`, { action },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      )
    } catch {} // never fail visibly
  }

  const handleSkip = async () => {
    await recordInteraction('dismissed')
    animateOut()
  }

  const handleGetAccess = async () => {
    await recordInteraction('clicked_purchase')
    animateOut()
    // This popup only ever renders for an already-logged-in, non-premium user
    // (see App.js's PromoPopupRenderer: `if (!user || user.is_premium) return null`),
    // so it must go to checkout, never back to signup.
    window.location.href = 'https://thachari.com/pricing'
  }

  const animateOut = () => {
    setAnimateIn(false)
    setTimeout(() => {
      setVisible(false)
      onClose?.()
    }, 300)
  }

  if (!visible) return null

  // Pricing
  const originalPrice = 9999
  const salePrice = 4999
  const seatsLeft = admissionCount?.remaining ?? null

  return (
    <>
      {previewMode && (
        <div style={{
          position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)',
          zIndex: 1001, background: '#111', color: '#fff', fontSize: 11,
          fontWeight: 700, letterSpacing: '0.08em', padding: '4px 12px',
          borderRadius: 999, textTransform: 'uppercase',
        }}>
          Preview Mode
        </div>
      )}

      {/* Backdrop */}
      <div
        onClick={handleSkip}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.55)',
          zIndex: 999,
          opacity: animateIn ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      />

      {/* Popup card */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: '50%',
        transform: animateIn
          ? 'translateX(-50%) translateY(0)'
          : 'translateX(-50%) translateY(100%)',
        transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
        zIndex: 1000,
        width: '100%',
        maxWidth: 480,
        background: '#fff',
        borderRadius: '20px 20px 0 0',
        boxShadow: '0 -8px 40px rgba(0,0,0,0.18)',
        overflow: 'hidden',
        maxHeight: '92vh',
        overflowY: 'auto',
      }}>

        {/* Top accent bar */}
        <div style={{
          height: 4,
          background: 'linear-gradient(90deg, #A67C52, #d4a76a, #A67C52)'
        }} />

        {/* Header */}
        <div style={{
          padding: '20px 20px 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div>
            <p style={{
              margin: 0, fontSize: 13, color: '#A67C52',
              fontWeight: 600, letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }}>
              🎯 Special Offer
            </p>
            <h2 style={{
              margin: '4px 0 0', fontSize: 20, fontWeight: 700,
              color: '#1a1a1a', lineHeight: 1.3
            }}>
              {popupConfig?.headline || 'Unlock Premium Access to All Courses'}
            </h2>
          </div>
          <button
            onClick={handleSkip}
            style={{
              background: '#f5f5f5',
              border: 'none',
              borderRadius: '50%',
              width: 32, height: 32,
              cursor: 'pointer',
              fontSize: 16,
              color: '#666',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginLeft: 12
            }}
          >✕</button>
        </div>

        {/* Content */}
        <div style={{ padding: '16px 20px 24px' }}>

          {/* Admission counter */}
          {seatsLeft !== null && seatsLeft <= 300 && (
            <div style={{
              background: '#fff8f0',
              border: '1px solid #f5c89a',
              borderRadius: 8,
              padding: '8px 12px',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <span style={{ fontSize: 18 }}>🔥</span>
              <span style={{ fontSize: 13, color: '#92400e', fontWeight: 600 }}>
                Only {seatsLeft} seats left at ₹4,999!
              </span>
            </div>
          )}

          {/* Pricing card */}
          <div style={{
            background: 'linear-gradient(135deg, #fdf8f4 0%, #fef3e8 100%)',
            border: '2px solid #A67C52',
            borderRadius: 12,
            padding: '16px',
            marginBottom: 16,
          }}>
            {/* Price display */}
            <div style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 10,
              marginBottom: 4
            }}>
              <span style={{
                fontSize: 15,
                color: '#999',
                textDecoration: 'line-through'
              }}>
                ₹{originalPrice.toLocaleString('en-IN')}
              </span>
              <span style={{
                fontSize: 28,
                fontWeight: 800,
                color: '#A67C52',
                lineHeight: 1
              }}>
                ₹{salePrice.toLocaleString('en-IN')}
              </span>
              <span style={{ fontSize: 13, color: '#888' }}>+ GST</span>
            </div>
            <p style={{
              margin: '0 0 14px',
              fontSize: 12,
              color: '#666'
            }}>
              ✓ All courses  ✓ Certificates  ✓ Premium access  ✓ Mobile app
            </p>

            {/* Primary CTA */}
            <button
              onClick={handleGetAccess}
              onTouchStart={handleGetAccess}
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #A67C52, #8b6340)',
                color: 'white',
                border: 'none',
                borderRadius: 10,
                padding: '14px',
                fontSize: 15,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                boxShadow: '0 4px 14px rgba(166,124,82,0.4)',
              }}
            >
              🔓 {popupConfig?.cta_primary || 'Get Premium Access Now'}
            </button>
          </div>

          {/* Skip text */}
          <button
            onClick={handleSkip}
            style={{
              background: 'none',
              border: 'none',
              width: '100%',
              textAlign: 'center',
              color: '#9ca3af',
              fontSize: 13,
              cursor: 'pointer',
              padding: '12px 0 0',
            }}
          >
            Maybe later — Skip for now
          </button>
        </div>
      </div>
    </>
  )
}
